import { SCROLL_PROFILE } from "./debug-flags";

/**
 * Scroll-experience profile. Enable with
 * `localStorage.setItem('ano-scroll-profile', '1')` then reload.
 *
 * Emits `[sc] <event> k=v …` lines from the scroll critical path:
 *
 *   [sc] tick top=4800 dir=up Δ=-32 distToTop=180 distToBot=12400
 *   [sc] shift-up amount=200 fromBounds=76:106 distToTop=180
 *   [sc] bump-limit distToTop=120
 *   [sc] wbk-commit ch=hiring bounds=76:106→26:106 anchor=ab12cd Δ=+412 dur=18ms
 *   [sc] cs-restore ch=hiring anchor=ab12cd Δ=+0 (channel-switch cdu apply)
 *
 * The long-task observer registered by `channel-switch-profile.ts`
 * also starts when this flag is on, so `[lt]` lines interleave for
 * hitch attribution. Reading top-to-bottom should tell you: scroll
 * fired → shift dispatched → bounds committed in X ms → long task X
 * blocked the main thread for Y ms covering that commit.
 *
 * Cost when off: a single boolean read per callsite.
 */

const now = (): number =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

export function scrollNow(): number {
  return SCROLL_PROFILE ? now() : 0;
}

function formatFields(fields: Record<string, unknown> | undefined): string {
  if (!fields) return "";
  const parts: string[] = [];
  for (const [k, v] of Object.entries(fields)) {
    if (v === undefined || v === null) continue;
    parts.push(`${k}=${typeof v === "string" ? v : JSON.stringify(v)}`);
  }
  return parts.length ? " " + parts.join(" ") : "";
}

export function scrollLog(
  event: string,
  fields?: Record<string, unknown>,
): void {
  if (!SCROLL_PROFILE) return;
  console.log(`[sc] ${event}${formatFields(fields)}`);
}

/** Measure-and-emit pair; returns the elapsed ms (or 0 when flag off). */
export function scrollLogDur(
  event: string,
  t0: number,
  fields?: Record<string, unknown>,
): number {
  if (!SCROLL_PROFILE) return 0;
  const dur = now() - t0;
  console.log(
    `[sc] ${event}${formatFields({ ...fields, dur: `${dur.toFixed(1)}ms` })}`,
  );
  return dur;
}

/**
 * Throttled scrollTop sampler. Rate-limited so a 60Hz raw scroll event
 * stream collapses to ~5/s of `[sc] tick` lines. Tracks direction and
 * deltas across samples so the log shows movement, not just position.
 */
const TICK_INTERVAL_MS = 200;
let lastTickAt = 0;
let lastTickTop = -1;

// ─── Row mount / hydration counters ──────────────────────────────
// Per-row instrumentation would emit 200+ lines per shift-up; instead
// we increment module counters and emit ONE aggregated `[sc] row-batch`
// line per ~50ms of activity. The user can then read it against the
// surrounding `[lt]` lines to see how much of a longtask is mounts
// (phase-1 row shell) vs hydrations (phase-2 MessageActions).
//
// Two distinct signals:
//   • mounts: a MessageRow useEffect([], []) ran — full row fiber
//     mounted, paints in phase 1.
//   • hydrated: usePostMountFrame's setState(true) ran for a Message
//     — phase-2 MessageActions just mounted on rAF+1.
//
// A typical 200-row shift-up looks like:
//   [sc] row-batch mounted=205 hydrated=0 over=12ms   ← phase 1
//   [sc] row-batch mounted=0   hydrated=205 over=18ms ← phase 2 (rAF later)
// — and the surrounding [lt] lines tell you how long each batch
// actually blocked the main thread.
const BATCH_FLUSH_MS = 50;
let mountedCount = 0;
let hydratedCount = 0;
let unmountedCount = 0;
let batchStartedAt = 0;
let batchTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleBatchFlush(): void {
  if (batchTimer !== null) return;
  if (batchStartedAt === 0) batchStartedAt = now();
  batchTimer = setTimeout(() => {
    const over = now() - batchStartedAt;
    console.log(
      `[sc] row-batch mounted=${mountedCount} hydrated=${hydratedCount}` +
        `${unmountedCount > 0 ? ` unmounted=${unmountedCount}` : ""}` +
        ` over=${over.toFixed(0)}ms`,
    );
    mountedCount = 0;
    hydratedCount = 0;
    unmountedCount = 0;
    batchStartedAt = 0;
    batchTimer = null;
  }, BATCH_FLUSH_MS);
}

/** Called once per MessageRow mount (useEffect with [] deps). */
export function scrollRowMounted(): void {
  if (!SCROLL_PROFILE) return;
  mountedCount += 1;
  scheduleBatchFlush();
}

/** Called when a Message's usePostMountFrame flips false → true. */
export function scrollRowHydrated(): void {
  if (!SCROLL_PROFILE) return;
  hydratedCount += 1;
  scheduleBatchFlush();
}

/** Called once per MessageRow unmount (useEffect cleanup). */
export function scrollRowUnmounted(): void {
  if (!SCROLL_PROFILE) return;
  unmountedCount += 1;
  scheduleBatchFlush();
}

export function scrollTick(
  scrollTop: number,
  scrollHeight: number,
  clientHeight: number,
): void {
  if (!SCROLL_PROFILE) return;
  const t = now();
  if (t - lastTickAt < TICK_INTERVAL_MS) return;
  const delta = lastTickTop < 0 ? 0 : scrollTop - lastTickTop;
  const dir = delta === 0 ? "idle" : delta < 0 ? "up" : "down";
  lastTickAt = t;
  lastTickTop = scrollTop;
  console.log(
    `[sc] tick top=${scrollTop.toFixed(0)} dir=${dir} Δ=${delta.toFixed(0)}` +
      ` distToTop=${scrollTop.toFixed(0)}` +
      ` distToBot=${(scrollHeight - scrollTop - clientHeight).toFixed(0)}`,
  );
}
